import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { BarChart3, BookOpen, Star, Loader2, TrendingUp, Library } from "lucide-react";
import { t, useLang } from "../lib/i18n";

/**
 * User-scoped reading stats. Reads `GET /me/stats` — same data-shape the
 * admin dashboard consumes, but filtered to the current user.
 */
export default function StatsPage() {
  useLang();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    api.get("/me/stats")
      .then(({ data }) => { if (alive) { setStats(data); setLoading(false); } })
      .catch((e) => { if (alive) { setError(formatApiError(e)); setLoading(false); } });
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-16 text-center">
        <Loader2 className="w-6 h-6 mx-auto text-[#8C7F65] animate-spin" strokeWidth={1.5} />
        <div className="font-mono uppercase tracking-widest text-xs text-[#525252] mt-4">{t("stats.loading")}</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-16">
        <div className="border border-[#B91C1C]/20 bg-[#B91C1C]/5 text-[#B91C1C] text-sm p-4 rounded-sm" data-testid="stats-error">{error}</div>
      </div>
    );
  }

  const empty = !stats?.total_books;

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-12 fade-up" data-testid="stats-page">
      <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#4A4030] inline-flex items-center gap-2">
        <BarChart3 className="w-3.5 h-3.5" strokeWidth={1.5} /> {t("stats.eyebrow")}
      </div>
      <h1 className="font-heading text-4xl md:text-5xl text-[#0A0A0A] mt-3 tracking-tight">{t("stats.title")}</h1>
      <p className="text-[#4A4030] mt-3 max-w-2xl">{t("stats.subtitle")}</p>

      {empty ? (
        <div className="mt-14 border border-dashed border-[rgba(0,0,0,0.14)] p-16 text-center">
          <BookOpen className="w-8 h-8 mx-auto text-[#8C7F65]" strokeWidth={1.5} />
          <div className="font-heading text-2xl text-[#525252] mt-4">{t("stats.empty_title")}</div>
          <p className="text-sm text-[#4A4030] mt-2 max-w-md mx-auto">{t("stats.empty_body")}</p>
          <Link
            to="/add"
            className="mt-6 inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-xs px-5 py-3 rounded-sm transition-colors"
            data-testid="stats-empty-add-book"
          >
            <BookOpen className="w-3.5 h-3.5" strokeWidth={1.5} /> {t("stats.empty_cta")}
          </Link>
        </div>
      ) : (
        <>
          {/* Big-number tiles */}
          <div className="mt-12 grid sm:grid-cols-2 md:grid-cols-4 gap-px bg-[rgba(0,0,0,0.08)] border border-[rgba(0,0,0,0.08)]" data-testid="stats-tiles">
            <BigTile label={t("stats.books_read")} value={stats.total_books} icon={BookOpen} testId="tile-books" />
            <BigTile label={t("stats.reviews_written")} value={stats.total_reviews} icon={Star} testId="tile-reviews" />
            <BigTile label={t("stats.pages_read")} value={stats.total_pages ? stats.total_pages.toLocaleString() : 0} icon={TrendingUp} testId="tile-pages" />
            <BigTile label={t("stats.avg_rating")} value={stats.avg_rating != null ? `${stats.avg_rating} / 5` : "—"} icon={Star} testId="tile-avg" />
          </div>

          {/* Rating histogram */}
          {stats.total_reviews > 0 && (
            <Card title={t("stats.rating_dist_title")} testId="rating-dist">
              <RatingBars dist={stats.rating_distribution || {}} />
            </Card>
          )}

          {/* Two-column: genres + authors */}
          <div className="mt-8 grid md:grid-cols-2 gap-px bg-[rgba(0,0,0,0.08)] border border-[rgba(0,0,0,0.08)]">
            <RankedList title={t("stats.top_genres")} rows={stats.top_genres || []} testId="top-genres" />
            <RankedList title={t("stats.top_authors")} rows={stats.top_authors || []} testId="top-authors" />
          </div>

          {/* Age ratings + series */}
          <div className="mt-8 grid md:grid-cols-2 gap-px bg-[rgba(0,0,0,0.08)] border border-[rgba(0,0,0,0.08)]">
            <RankedList title={t("stats.age_ratings")} rows={stats.age_ratings || []} testId="age-ratings" />
            <Card title={t("stats.series_title")} testId="series-card">
              <div className="flex items-baseline gap-3">
                <Library className="w-5 h-5 text-[#8C7F65]" strokeWidth={1.5} />
                <div className="font-heading text-4xl text-[#0A0A0A]" data-testid="series-tracked">{stats.series_tracked ?? 0}</div>
                <div className="font-mono uppercase tracking-widest text-[10px] text-[#525252]">{t("stats.series_tracked")}</div>
              </div>
              {stats.longest_series && (
                <div className="mt-4 pt-4 border-t border-[rgba(10,10,10,0.08)]">
                  <div className="font-mono uppercase tracking-widest text-[10px] text-[#8C7F65]">{t("stats.longest_series")}</div>
                  <div className="font-heading text-xl text-[#0A0A0A] mt-1" data-testid="longest-series">
                    &ldquo;{stats.longest_series.name}&rdquo; · {stats.longest_series.count} {t("stats.books")}
                  </div>
                </div>
              )}
            </Card>
          </div>

          {/* Books per year */}
          {stats.books_per_year && stats.books_per_year.length > 0 && (
            <Card title={t("stats.per_year_title")} testId="per-year-card">
              <YearBars data={stats.books_per_year} />
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// -------------------------------------------------------------- primitives

function BigTile({ label, value, icon: Icon, testId }) {
  return (
    <div className="bg-[#FAF5E6] p-8" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="font-mono uppercase tracking-widest text-[10px] text-[#525252]">{label}</div>
        <Icon className="w-4 h-4 text-[#8C7F65]" strokeWidth={1.5} />
      </div>
      <div className="font-heading text-4xl md:text-5xl text-[#0A0A0A] mt-3">{value}</div>
    </div>
  );
}

function Card({ title, testId, children }) {
  return (
    <div className="mt-8 bg-[#FAF5E6] border border-[rgba(10,10,10,0.10)] p-8" data-testid={testId}>
      <div className="font-mono uppercase tracking-widest text-[10px] text-[#525252] mb-5">{title}</div>
      {children}
    </div>
  );
}

function RankedList({ title, rows, testId }) {
  const top = (rows || []).slice(0, 6);
  const max = top.length ? Math.max(...top.map(([, n]) => n)) : 1;
  return (
    <div className="bg-[#FAF5E6] p-8" data-testid={testId}>
      <div className="font-mono uppercase tracking-widest text-[10px] text-[#525252] mb-5">{title}</div>
      {top.length === 0 ? (
        <div className="text-sm text-[#8C7F65]">{t("stats.nothing_yet")}</div>
      ) : (
        <ul className="space-y-3">
          {top.map(([label, n], i) => (
            <li key={label} data-testid={`${testId}-row-${i}`}>
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[#0A0A0A] truncate">{label}</span>
                <span className="font-mono text-xs text-[#525252]">{n}</span>
              </div>
              <div className="mt-1.5 h-1 bg-[rgba(10,10,10,0.08)]">
                <div className="h-full bg-[#0A0A0A]" style={{ width: `${(n / max) * 100}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RatingBars({ dist }) {
  const entries = [5, 4, 3, 2, 1].map((n) => [n, Number(dist?.[n] || 0)]);
  const max = Math.max(...entries.map(([, v]) => v), 1);
  return (
    <ul className="space-y-3">
      {entries.map(([star, count]) => (
        <li key={star} className="flex items-center gap-3" data-testid={`rating-row-${star}`}>
          <span className="font-mono text-xs text-[#525252] w-6">{star}★</span>
          <div className="flex-1 h-2 bg-[rgba(10,10,10,0.08)]">
            <div className="h-full bg-[#0A0A0A]" style={{ width: `${(count / max) * 100}%` }} />
          </div>
          <span className="font-mono text-xs text-[#525252] w-8 text-right">{count}</span>
        </li>
      ))}
    </ul>
  );
}

function YearBars({ data }) {
  const max = Math.max(...data.map(([, n]) => n), 1);
  return (
    <div className="flex items-end gap-3 min-h-[140px]" data-testid="year-bars">
      {data.map(([year, n]) => (
        <div key={year} className="flex-1 flex flex-col items-center gap-2">
          <div className="w-full flex flex-col justify-end h-32">
            <div className="w-full bg-[#0A0A0A]" style={{ height: `${(n / max) * 100}%` }} />
          </div>
          <div className="font-mono text-[10px] text-[#525252]">{year}</div>
          <div className="font-mono text-xs text-[#0A0A0A]">{n}</div>
        </div>
      ))}
    </div>
  );
}
